import { useCallback, useEffect } from 'react';

import { mergeRestaurantData } from 'features/store/utils';

import { IRestaurant } from '@rbi-ctg/store';
import { getRestaurantsByStoreNumber } from 'remote/api/restaurants';
import { useNetworkContext } from 'state/network';
import { addLoggingContext } from 'utils/logger';

import { useGetStoreFn } from './use-get-store-fn';
import { usePosDataQuery } from './use-pos-data-query';

export const useStoreUtils = ({ store }: { store: IRestaurant | null }) => {
  const { sanityEndpoints } = useNetworkContext();
  const getRestaurant = useGetStoreFn();

  const { data, isLoading: isPosDataLoading, refetch: refetchPosData } = usePosDataQuery({
    storeNumber: store?.number ?? null,
  });

  const prices = store?._id && data?.posData && !isPosDataLoading ? data.posData : null;

  const storeEmail = store?.email?.[0] ?? '';

  const fetchStore = useCallback(
    async (storeId: string) => {
      const rbiRestaurant = await getRestaurant(storeId);

      if (!rbiRestaurant) {
        return;
      }

      const [sanityStore] = await getRestaurantsByStoreNumber(sanityEndpoints.groq, {
        storeNumber: storeId,
      });

      return mergeRestaurantData({ rbiRestaurant: rbiRestaurant as IRestaurant, sanityStore });
    },
    [sanityEndpoints.groq] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(
    () =>
      addLoggingContext({
        storeId: store?.number ?? 'No store selected',
      }),
    [store]
  );

  return {
    email: storeEmail,
    prices,
    isPosDataLoading,
    refetchPosData,
    fetchStore,
  };
};
